import type { Notification as AppNotification } from "../types/notification";

type BrowserNotificationPermission = "default" | "denied" | "granted";

const DEDUP_STORAGE_KEY = "dwelldrop:notif-shown";
const DEDUP_TTL_MS = 60_000;

function isBrowserNotificationSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export async function requestBrowserNotificationPermission(): Promise<BrowserNotificationPermission | "unsupported"> {
  if (!isBrowserNotificationSupported()) {
    return "unsupported";
  }

  if (window.Notification.permission === "default") {
    try {
      return await window.Notification.requestPermission();
    } catch {
      return window.Notification.permission;
    }
  }

  return window.Notification.permission;
}

function claimNotificationId(id: string): boolean {
  if (typeof window === "undefined" || !window.localStorage) {
    return true;
  }

  try {
    const now = Date.now();
    const raw = window.localStorage.getItem(DEDUP_STORAGE_KEY);
    const map: Record<string, number> = raw ? JSON.parse(raw) : {};

    if (map[id] && now - map[id] < DEDUP_TTL_MS) {
      return false;
    }

    for (const key of Object.keys(map)) {
      if (now - map[key] > DEDUP_TTL_MS) {
        delete map[key];
      }
    }

    map[id] = now;
    window.localStorage.setItem(DEDUP_STORAGE_KEY, JSON.stringify(map));
    return true;
  } catch {
    return true;
  }
}

export function showBrowserNotification(notification: AppNotification): void {
  if (!isBrowserNotificationSupported()) {
    return;
  }

  if (window.Notification.permission !== "granted") {
    return;
  }

  if (typeof document !== "undefined" && document.visibilityState === "visible") {
    return;
  }

  if (!claimNotificationId(notification.id)) {
    return;
  }

  const options: NotificationOptions = {
    body: notification.body,
    tag: notification.id,
    icon: "/favicon.svg",
    badge: "/favicon.svg",
  };

  if (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator
  ) {
    navigator.serviceWorker.ready
      .then((registration) => registration.showNotification(notification.title, options))
      .catch(() => {
        try {
          new window.Notification(notification.title, options);
        } catch {
          // ignore
        }
      });
    return;
  }

  try {
    new window.Notification(notification.title, options);
  } catch {
    // ignore
  }
}
